const CACHE_NAME = 'atp-offline-episodes-v1';
const DB_NAME = 'atp-offline-db';
const DB_STORE = 'episodes';
const DB_VERSION = 1;

export const QUOTA_WARNING_PERCENT = 0.85;
export const QUOTA_CRITICAL_PERCENT = 0.95;

export interface OfflineEpisodeMeta {
  episodeId: string;
  audioUrl: string;
  title: string;
  podcastName: string;
  artworkUrl?: string;
  durationSeconds?: number;
  savedAt: number;
  sizeBytes: number;
}

export interface OfflineStorageEstimate {
  usage: number;
  quota: number;
  percent: number;
  available: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function supportsOfflineStorage(): boolean {
  return 'caches' in window && 'indexedDB' in window;
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(DB_STORE)) {
        db.createObjectStore(DB_STORE, { keyPath: 'episodeId' });
      }
    };
  });
  return dbPromise;
}

export async function getOfflineStorageEstimate(): Promise<OfflineStorageEstimate | null> {
  if (!navigator.storage || !navigator.storage.estimate) return null;
  try {
    const est = await navigator.storage.estimate();
    const usage = est.usage || 0;
    const quota = est.quota || 0;
    return {
      usage,
      quota,
      available: Math.max(0, quota - usage),
      percent: quota > 0 ? usage / quota : 0,
    };
  } catch {
    return null;
  }
}

export async function isEpisodeOffline(episodeId: string): Promise<boolean> {
  if (!episodeId) return false;
  try {
    const db = await openDb();
    const tx = db.transaction(DB_STORE, 'readonly');
    const store = tx.objectStore(DB_STORE);
    const req = store.get(episodeId);
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(!!req.result);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return false;
  }
}

export async function getOfflineEpisodeMeta(episodeId: string): Promise<OfflineEpisodeMeta | null> {
  if (!episodeId) return null;
  try {
    const db = await openDb();
    const tx = db.transaction(DB_STORE, 'readonly');
    const store = tx.objectStore(DB_STORE);
    const req = store.get(episodeId);
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve((req.result as OfflineEpisodeMeta) || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function getOfflineEpisodes(): Promise<OfflineEpisodeMeta[]> {
  try {
    const db = await openDb();
    const tx = db.transaction(DB_STORE, 'readonly');
    const store = tx.objectStore(DB_STORE);
    const req = store.getAll();
    return new Promise((resolve, reject) => {
      req.onsuccess = () => {
        const results = ((req.result as OfflineEpisodeMeta[]) || []).sort(
          (a, b) => b.savedAt - a.savedAt,
        );
        resolve(results);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

export async function getOfflineAudioUrl(episodeId: string): Promise<string | null> {
  if (!episodeId) return null;
  try {
    const meta = await getOfflineEpisodeMeta(episodeId);
    if (!meta) return null;
    const cache = await caches.open(CACHE_NAME);
    const response = await cache.match(meta.audioUrl);
    if (!response) return null;
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * "Orphaned" = we have IndexedDB metadata saying the episode is saved offline,
 * but the underlying audio Blob has been evicted from the Cache Storage by the
 * browser (common when device storage gets tight). The UI uses this to prompt
 * a re-download rather than silently failing playback.
 */
export async function isOfflineDownloadOrphaned(episodeId: string): Promise<boolean> {
  if (!episodeId) return false;
  try {
    const meta = await getOfflineEpisodeMeta(episodeId);
    if (!meta) return false;
    const cache = await caches.open(CACHE_NAME);
    const response = await cache.match(meta.audioUrl);
    return !response;
  } catch {
    return false;
  }
}


async function putMeta(meta: OfflineEpisodeMeta): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(DB_STORE, 'readwrite');
  const store = tx.objectStore(DB_STORE);
  store.put(meta);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function deleteMeta(episodeId: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(DB_STORE, 'readwrite');
  const store = tx.objectStore(DB_STORE);
  store.delete(episodeId);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function removeOfflineEpisode(episodeId: string): Promise<void> {
  if (!episodeId) return;
  const meta = await getOfflineEpisodeMeta(episodeId);
  if (meta) {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.delete(meta.audioUrl);
    } catch (e) {
      console.warn('Failed to delete cached audio blob:', e);
    }
  }
  await deleteMeta(episodeId);
}

// Podcast CDNs almost never set Access-Control-Allow-Origin, so a direct
// browser fetch fails with Safari's opaque "Load failed" / Chrome's
// "Failed to fetch". We route every offline download through our edge proxy
// which adds CORS + streams the bytes. Built from VITE_SUPABASE_URL so it
// works in preview, prod, and the installed PWA.
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string | undefined;
function proxiedAudioUrl(rawUrl: string): string {
  if (!SUPABASE_URL) return rawUrl;
  return `${SUPABASE_URL}/functions/v1/proxy-episode-audio?url=${encodeURIComponent(rawUrl)}`;
}

async function fetchSizeEstimate(audioUrl: string, signal?: AbortSignal): Promise<number | null> {
  try {
    const head = await fetch(proxiedAudioUrl(audioUrl), { method: 'HEAD', signal });
    if (head.ok) {
      const len = Number(head.headers.get('content-length') || 0);
      if (len > 0) return len;
    }
  } catch {
    // HEAD not supported / blocked — fall through to default estimate.
  }
  return null;
}

export class OfflineDownloadAbortError extends Error {
  constructor() {
    super('Offline download was canceled.');
    this.name = 'OfflineDownloadAbortError';
  }
}

export async function downloadEpisode(
  meta: Omit<OfflineEpisodeMeta, 'savedAt' | 'sizeBytes'>,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  if (!supportsOfflineStorage()) {
    throw new Error('Offline storage is not supported in this browser.');
  }

  if (signal?.aborted) throw new OfflineDownloadAbortError();

  const estimate = await getOfflineStorageEstimate();
  if (estimate) {
    if (estimate.percent >= QUOTA_CRITICAL_PERCENT) {
      throw new Error(
        'Your device storage is almost full. Free up space before downloading more episodes.',
      );
    }

    const sizeEstimate = (await fetchSizeEstimate(meta.audioUrl, signal)) || 60 * 1024 * 1024;
    if (estimate.available < sizeEstimate) {
      throw new Error(
        'Not enough free space on this device to download this episode. Try removing older downloads first.',
      );
    }
  }

  const fetchUrl = proxiedAudioUrl(meta.audioUrl);
  let res: Response;
  try {
    res = await fetch(fetchUrl, { signal });
  } catch (err) {
    if ((err as any)?.name === 'AbortError') throw new OfflineDownloadAbortError();
    throw new Error(
      'Could not reach the audio file. Check your connection and try again.',
    );
  }
  if (!res.ok || !res.body) {
    throw new Error(`Failed to download episode audio: ${res.status} ${res.statusText}`);
  }

  const contentLength = Number(res.headers.get('content-length') || 0);
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;

  const onAbort = () => {
    reader.cancel().catch(() => {});
  };
  signal?.addEventListener('abort', onAbort);

  try {
    while (true) {
      if (signal?.aborted) throw new OfflineDownloadAbortError();
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      if (contentLength && onProgress) {
        onProgress(Math.min(100, Math.round((received / contentLength) * 100)));
      }
    }
  } catch (err) {
    if (signal?.aborted || (err as any)?.name === 'AbortError') {
      throw new OfflineDownloadAbortError();
    }
    throw err;
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }


  // Combine chunks into a single buffer without copying per chunk via repeated slicing.
  const all = new Uint8Array(received);
  let pos = 0;
  for (const chunk of chunks) {
    all.set(chunk, pos);
    pos += chunk.length;
  }

  const blob = new Blob([all]);
  const cache = await caches.open(CACHE_NAME);
  await cache.put(
    meta.audioUrl,
    new Response(blob, {
      headers: {
        'content-type': res.headers.get('content-type') || 'audio/mpeg',
        'content-length': String(blob.size),
      },
    }),
  );

  await putMeta({
    ...meta,
    savedAt: Date.now(),
    sizeBytes: blob.size,
  });
}
